import { useCallback, useRef } from "react";
import { useUndoRedo } from "./useUndoRedo";

interface AudioData {
  channels: Float32Array[];
  sampleRate: number;
}

interface UseAudioEditorResult {
  audioData: AudioData | null;
  setAudioBuffer: (buffer: AudioBuffer) => void;
  insertAudioAt: (buffer: AudioBuffer, positionSeconds: number) => void;
  deleteRegion: (startTime: number, endTime: number) => void;
  moveRegion: (startTime: number, endTime: number, toTime: number) => void;
  toWavBlob: () => Blob | null;
  getDuration: () => number;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  reset: () => void;
  /** Increments on every state change so wavesurfer can re-init */
  version: number;
}

export function useAudioEditor(): UseAudioEditorResult {
  const versionRef = useRef(0);
  const {
    state: audioData,
    setState: setAudioData,
    undo: undoRaw,
    redo: redoRaw,
    canUndo,
    canRedo,
    reset: resetUndoRedo,
  } = useUndoRedo<AudioData | null>(null, { maxHistory: 15 });

  const undo = useCallback(() => {
    undoRaw();
    versionRef.current++;
  }, [undoRaw]);

  const redo = useCallback(() => {
    redoRaw();
    versionRef.current++;
  }, [redoRaw]);

  const setAudioBuffer = useCallback(
    (buffer: AudioBuffer) => {
      const channels: Float32Array[] = [];
      for (let i = 0; i < buffer.numberOfChannels; i++) {
        channels.push(new Float32Array(buffer.getChannelData(i)));
      }
      resetUndoRedo({ channels, sampleRate: buffer.sampleRate });
      versionRef.current++;
    },
    [resetUndoRedo]
  );

  const insertAudioAt = useCallback(
    (buffer: AudioBuffer, positionSeconds: number) => {
      const newChannels: Float32Array[] = [];
      for (let i = 0; i < buffer.numberOfChannels; i++) {
        newChannels.push(new Float32Array(buffer.getChannelData(i)));
      }
      const newSampleRate = buffer.sampleRate;

      setAudioData((prev) => {
        if (!prev) {
          // No existing audio — just set it
          versionRef.current++;
          return { channels: newChannels, sampleRate: newSampleRate };
        }

        const insertSample = Math.min(
          Math.floor(positionSeconds * prev.sampleRate),
          prev.channels[0].length
        );
        const existingLen = prev.channels[0].length;
        const newLen = newChannels[0].length;

        // Resample new audio if sample rates differ
        let insertChannels = newChannels;
        let insertLen = newLen;
        if (newSampleRate !== prev.sampleRate) {
          const ratio = prev.sampleRate / newSampleRate;
          insertLen = Math.floor(newLen * ratio);
          insertChannels = newChannels.map((ch) => {
            const resampled = new Float32Array(insertLen);
            for (let i = 0; i < insertLen; i++) {
              const srcIdx = i / ratio;
              const lo = Math.floor(srcIdx);
              const hi = Math.min(lo + 1, ch.length - 1);
              const frac = srcIdx - lo;
              resampled[i] = ch[lo] * (1 - frac) + ch[hi] * frac;
            }
            return resampled;
          });
        }

        // Match channel counts (duplicate mono → stereo, or take first channel)
        const numChannels = prev.channels.length;
        const merged = prev.channels.map((existingCh, chIdx) => {
          const insertCh =
            chIdx < insertChannels.length
              ? insertChannels[chIdx]
              : insertChannels[0]; // duplicate mono if needed
          const result = new Float32Array(existingLen + insertLen);
          result.set(existingCh.subarray(0, insertSample));
          result.set(insertCh, insertSample);
          result.set(existingCh.subarray(insertSample), insertSample + insertLen);
          return result;
        });

        versionRef.current++;
        return { channels: merged, sampleRate: prev.sampleRate };
      });
    },
    [setAudioData]
  );

  const deleteRegion = useCallback(
    (startTime: number, endTime: number) => {
      setAudioData((prev) => {
        if (!prev) return prev;
        const startSample = Math.floor(startTime * prev.sampleRate);
        const endSample = Math.floor(endTime * prev.sampleRate);
        const totalSamples = prev.channels[0].length;

        if (startSample >= endSample || startSample >= totalSamples) return prev;

        const clampedEnd = Math.min(endSample, totalSamples);
        const newLength = totalSamples - (clampedEnd - startSample);

        const newChannels = prev.channels.map((channel) => {
          const newChannel = new Float32Array(newLength);
          newChannel.set(channel.subarray(0, startSample));
          newChannel.set(channel.subarray(clampedEnd), startSample);
          return newChannel;
        });

        versionRef.current++;
        return { channels: newChannels, sampleRate: prev.sampleRate };
      });
    },
    [setAudioData]
  );

  const moveRegion = useCallback(
    (startTime: number, endTime: number, toTime: number) => {
      setAudioData((prev) => {
        if (!prev) return prev;
        const sr = prev.sampleRate;
        const startSample = Math.floor(startTime * sr);
        const endSample = Math.min(Math.floor(endTime * sr), prev.channels[0].length);
        const regionLen = endSample - startSample;
        if (regionLen <= 0) return prev;

        // Extract the region, then remove it from the original
        const newChannels = prev.channels.map((ch) => {
          const region = ch.slice(startSample, endSample);
          const without = new Float32Array(ch.length - regionLen);
          without.set(ch.subarray(0, startSample));
          without.set(ch.subarray(endSample), startSample);

          // Calculate insert position in the "without" array
          let insertSample = Math.floor(toTime * sr);
          // Adjust if the destination was after the removed region
          if (insertSample > startSample) {
            insertSample = Math.max(0, insertSample - regionLen);
          }
          insertSample = Math.min(insertSample, without.length);

          const result = new Float32Array(ch.length);
          result.set(without.subarray(0, insertSample));
          result.set(region, insertSample);
          result.set(without.subarray(insertSample), insertSample + regionLen);
          return result;
        });

        versionRef.current++;
        return { channels: newChannels, sampleRate: sr };
      });
    },
    [setAudioData]
  );

  const getDuration = useCallback(() => {
    if (!audioData) return 0;
    return audioData.channels[0].length / audioData.sampleRate;
  }, [audioData]);

  const toWavBlob = useCallback(() => {
    if (!audioData) return null;

    const { channels, sampleRate } = audioData;
    const numChannels = channels.length;
    const numSamples = channels[0].length;
    const bytesPerSample = 2;
    const dataLength = numSamples * numChannels * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataLength);
    const view = new DataView(buffer);

    const writeString = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };

    writeString(0, "RIFF");
    view.setUint32(4, 36 + dataLength, true);
    writeString(8, "WAVE");
    writeString(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
    view.setUint16(32, numChannels * bytesPerSample, true);
    view.setUint16(34, bytesPerSample * 8, true);
    writeString(36, "data");
    view.setUint32(40, dataLength, true);

    let offset = 44;
    for (let i = 0; i < numSamples; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(-1, Math.min(1, channels[ch][i]));
        view.setInt16(
          offset,
          sample < 0 ? sample * 0x8000 : sample * 0x7fff,
          true
        );
        offset += 2;
      }
    }

    return new Blob([buffer], { type: "audio/wav" });
  }, [audioData]);

  const reset = useCallback(() => {
    resetUndoRedo(null);
    versionRef.current++;
  }, [resetUndoRedo]);

  return {
    audioData,
    setAudioBuffer,
    insertAudioAt,
    deleteRegion,
    moveRegion,
    toWavBlob,
    getDuration,
    undo,
    redo,
    canUndo,
    canRedo,
    reset,
    version: versionRef.current,
  };
}
